import Vue from 'vue'
import { arrayFind } from 'util/ts-utils'
import { callTurnToConfigFn } from 'util/turnto-utils'
import DimDistribution from 'components/reviews-summary/dim-distribution'
import domUtils from 'util/dom-utils'
import { localizedRating } from 'util/localization-utils'
import logger from 'util/logger'
import globalStore from 'init/global-store'
import reviewsSummaryService from 'services/api/reviews-summary'
import { RestError, SummaryDimension, SubdimensionTeaserConfig }
  from 'services/api-service'
import SummaryDimRange from 'components/reviews-summary/summary-dim-range'
import SummaryDimRating from 'components/reviews-summary/summary-dim-rating'
import { turnToConfig } from 'init/config'

import withRender from './subdimension-teaser.html'
import './subdimension-teaser.scss'


export default withRender(Vue.extend({
  name: 'SubdimensionTeaser',
  data() {
    return {
      customDimensions: [],
      globalStore,
      hide: false
    }
  },
  props: {
    // since there can be multiple subdimension teasers per page, the config is passed in
    teaserConfig: Object
  },
  components: {
    'tt-summary-custom-range': SummaryDimRange,
    'tt-summary-custom-rating': SummaryDimRating,
    'tt-dim-distribution': DimDistribution
  },
  computed: {
    currentSku(): string {
      return this.globalStore.sku
    },
    lang() {
      return turnToConfig.locale.substring(0, 2)
    },
    subdims() {
      return (this.teaserConfig as SubdimensionTeaserConfig).subdimensions.map((dimConfig) => {
        const customDim = arrayFind(this.customDimensions, (dim: SummaryDimension) => {
          return dim.dimensionDefinitionId === dimConfig.dimId
        }) as SummaryDimension
        const totalReviewsForDim =
          customDim ? customDim.values.reduce((sum, b) => sum + b.count, 0) : 0
        if (totalReviewsForDim < this.teaserConfig.minReviews) {
          return undefined
        }
        const isRating = customDim.type === 1
        if (dimConfig.displayType === 'TEXT') {
          let valueLabel
          if (isRating) {
            valueLabel = localizedRating(customDim.average)
          } else {
            valueLabel = customDim.values.reduce((acc, v) => {
              return v.count > acc.count ? v : acc
            }, { count: 0 }).label
          }
          return {
            dim: customDim,
            displayType: dimConfig.displayType,
            label: customDim.teaserLabel || `${customDim.label}:`,
            valueLabel
          }
        }
        return {
          dim: customDim,
          displayType: dimConfig.displayType,
          label: customDim.teaserLabel || `${customDim.label}${isRating ? ':' : ''}`,
          rating: isRating ? localizedRating(customDim.average) : undefined,
          numReviewsMsg: this.$msg('dimTeaser.reviews', [totalReviewsForDim], totalReviewsForDim)
        }
      }).filter(subDim => subDim !== undefined)
    }
  },
  methods: {
    fetchData() {
      if (this.currentSku) {
        reviewsSummaryService.reviewsSummary({
          sku: this.currentSku
        }).then((data) => {
          if (!data) { // invalid sku most likely
            return
          }
          this.customDimensions = data.customDimensions
          this.hide = data.hideDisplay
          this.$nextTick(() => { callTurnToConfigFn('subdimensionTeaser', 'onFinish') })
        }).catch((e: RestError) => {
          if (e.errorCode === 'turnto-offline') {
            logger.info('TurnTo is in offline mode - Reviews Summary not loaded', e)
          } else {
            logger.error('Error loading data for the Reviews Summary widget', e)
          }
        })
      }
    },
    toReviews(e: Event) {
      e.preventDefault()
      const config = window.turnToConfig.subdimensionTeaser || {}
      if (config.showReviews) {
        config.showReviews()
        return
      }
      const elem = document.querySelector('#tt-reviews-list')
      if (elem) {
        domUtils.scrollToElement(elem)
        // not ideal, but since we don't know when scroll is done, just going to wait for a second
        setTimeout(() => {
          domUtils.safeFocus(elem.querySelector('.tt-c-reviews-list__heading'))
        }, 1000)
      }
    }
  },
  watch: {
    // whenever the currentSku changes, we need to reload the comments
    currentSku() {
      this.fetchData()
    }
  },
  created() {
    this.fetchData()
  }
}))
